import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { EMPTY, from, of, throwError } from 'rxjs'
import { catchError, endWith, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import {
  approveMontshipBooking,
  approveMontshipBookingFailure,
  approveMontshipBookingSuccess,
  batchCreateMontshipBookings,
  batchCreateMontshipBookingsFailure,
  batchCreateMontshipBookingsSuccess,
  createMontshipBooking,
  createMontshipBookingFailure,
  createMontshipBookingSuccess,
  montshipBookingResolved,
  putMontshipBookingOnHold,
  putMontshipBookingOnHoldFailure,
  putMontshipBookingOnHoldSuccess,
  rejectMontshipBooking,
  rejectMontshipBookingFailure,
  rejectMontshipBookingSuccess,
} from './montship-booking.actions'
import { MissingBookingDataError, MontshipBookingService } from 'src/services/data/montship-booking.service'
import { BusService } from 'src/services/bus.service'
import { BusActions } from 'src/constants/bus-events'
import { sortBy } from 'lodash-es'
import { sendAndWatch } from 'src/shared/utils/send-and-watch'

const isDialogDismissal = (error: unknown) =>
  error === 'cancel' || error === 'backdrop click' || error === 'escape key press'

@Injectable()
export class MontshipBookingEffects {
  private actions$ = inject(Actions)
  private bookings = inject(MontshipBookingService)
  private toaster = inject(ToasterService)
  private bus = inject(BusService)


  createMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(createMontshipBooking),
    mergeMap(action =>
      sendAndWatch(this.bus, this.toaster, {
        action: BusActions.MONTSHIP_BOOKING,
        dealIds: [action.deal_id],
        request: operationId => this.bookings.sendRequest({ deal_id: action.deal_id, request: action.request }, operationId),
        acceptedDealIds: () => [action.deal_id],
        messages: { system: 'Montship', subject: 'booking', submitted: 'Montship booking request submitted' },
      }).pipe(
        map(booking => createMontshipBookingSuccess({ booking })),
        endWith(montshipBookingResolved({ dealId: action.deal_id })),
        catchError(error => {
          console.error('Unable to send Montship booking request', error)
          this.toaster.error('Unable to send Montship booking request', error)
          return of(createMontshipBookingFailure({ error }))
  })))))

  batchCreateMontshipBookings$ = createEffect(() => this.actions$.pipe(
    ofType(batchCreateMontshipBookings),
    mergeMap(action =>
      from(this.bookings.confirmBatchRequest()).pipe(
        catchError(error => {
          if (!isDialogDismissal(error)) console.error('Unable to confirm Montship booking requests', error)
          return EMPTY
        }),
        switchMap(() => sendAndWatch(this.bus, this.toaster, {
          action: BusActions.MONTSHIP_BOOKING,
          dealIds: action.dealIds,
          request: operationId => this.bookings.createBatchRequest(action.dealIds, operationId),
          acceptedDealIds: bookings => (bookings || []).map(b => b.deal_id),
          messages: { system: 'Montship', subject: 'booking', submitted: 'Montship booking requests submitted' },
        }).pipe(
          map(bookings => batchCreateMontshipBookingsSuccess({ bookings })),
          endWith(...action.dealIds.map(dealId => montshipBookingResolved({ dealId }))))),
        catchError(error => {
          if (error instanceof MissingBookingDataError) {
            this.toaster.warning('No bookings submitted due to missing or incorrect data', `Please review or manually submit the following deals: ${error.dealIds.join(', ')}`);
            return of(batchCreateMontshipBookingsFailure({ error }));
          }

          if (error.status === 400) {
            const validatorMessage = error.error?.error?.message;
            const matches = validatorMessage?.match(/requests\.(\d+)\./g);
            const dealIndices = [...new Set(matches?.map(match => parseInt(match?.split('.')[1])))];
            const dealIds = sortBy(action?.dealIds);
            const failedDealIds = dealIndices?.map(index => dealIds?.[index as number]);
            if (failedDealIds?.length) {
              this.toaster.warning('No bookings submitted due to missing or incorrect data', `Please review or manually submit the following deals: ${failedDealIds?.join(', ')}`);
              return of(batchCreateMontshipBookingsFailure({ error }));
            }
          }

          console.error('Unable to send Montship booking requests', error);
          this.toaster.error('Unable to send Montship booking requests', error);
          return of(batchCreateMontshipBookingsFailure({ error }));
        })
      )
    )
  ));

  approveMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(approveMontshipBooking),
    switchMap(action =>
      from(this.bookings.approve(action.bookingId)).pipe(
        map(response => approveMontshipBookingSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking approved successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to approve Montship booking', error)
          this.toaster.error('Unable to approve Montship booking', error)
          return of(approveMontshipBookingFailure({ error }))
  })))))

  rejectMontshipBooking$ = createEffect(() => this.actions$.pipe(
    ofType(rejectMontshipBooking),
    switchMap(action =>
      from(this.bookings.reject(action.bookingId)).pipe(
        map(response => rejectMontshipBookingSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking rejected successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to reject Montship booking', error)
          this.toaster.error('Unable to reject Montship booking', error)
          return of(rejectMontshipBookingFailure({ error }))
  })))))

  putMontshipBookingOnHold$ = createEffect(() => this.actions$.pipe(
    ofType(putMontshipBookingOnHold),
    switchMap(action =>
      from(this.bookings.putOnHold(action.bookingId)).pipe(
        map(response => putMontshipBookingOnHoldSuccess({ booking: response })),
        tap(() => this.toaster.success('Montship booking put on hold successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to put Montship booking on hold', error)
          this.toaster.error('Unable to put Montship booking on hold', error)
          return of(putMontshipBookingOnHoldFailure({ error }))
  })))))
}
